import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { OrderablePackage, PackagePickFilters, PackagePickSort } from '@mypoz/shared';
import {
  clampPackagePage,
  EMPTY_PACKAGE_PICK_FILTERS,
  filterPackagePicks,
  ORDER_PACKAGES_PER_PAGE,
  PACKAGE_PICK_SORTS,
  packagePageCount,
  pageOf,
} from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Card, Icon, cn } from '@mypoz/ui';

import { MAX_PACKAGES_PER_ORDER } from '@/features/orders/fixtures.js';
import { PackageRow } from '@/features/orders/components/PackageRow.js';
import {
  PickerChips,
  PickerEmpty,
  PickerSearch,
  PickerSort,
} from '@/features/orders/components/PickerControls.js';
import { Pager } from '@/components/Pager.js';
import { buttonClass } from '@/lib/styles.js';
import { PACKAGES_ROUTE } from '@/shell/navigation.js';
import { useFormat } from '@/lib/format.js';
import type { ReceiptSlot } from '@/features/orders/useReceiptUploads.js';

/** What every package list on an order shows next to each row: ticks, receipts and the limit. */
export interface PackageRowsProps {
  selectedIds: readonly string[];
  receipts?: Readonly<Record<string, readonly ReceiptSlot[]>>;
  onToggle: (packageId: string) => void;
  /** Uploads a receipt for a package (MYP-96). Given on every new order. */
  onAttach?: (packageId: string, file: File) => void;
  /**
   * The edit flow: `order_modify` takes no receipt, so the upload button is drawn locked with a
   * note instead of working (see `PackageRow.receiptsLocked`).
   */
  receiptsLocked?: boolean;
  onRetryReceipt?: (packageId: string, slotId: string) => void;
  onRemoveReceipt?: (packageId: string, slotId: string) => void;
  /**
   * Whether a ticked package needs a receipt (MYP-140): its warehouse needs them, or insurance is
   * chosen. A package that does not gets no receipt control at all. Omitted: every one does.
   */
  wantsReceipt?: (item: OrderablePackage) => boolean;
  /**
   * Why receipts are being asked for, said once under the list: the warehouse needs them, or the
   * insurance does. Omitted when no ticked package needs one.
   */
  receiptReason?: 'warehouse' | 'insurance';
  /** Receipts the order already has, by package id (Edit order): shown, not changed. */
  savedReceipts?: Readonly<Record<string, readonly string[]>>;
}

export interface PackagesStepProps extends PackageRowsProps {
  /** Everything tickable. Edit mode widens this to include the order's own packages. */
  pool: readonly OrderablePackage[];
  /**
   * Where the pool has got to (MYP-90). An empty pool means three different things — still
   * loading, failed, or genuinely nothing orderable — and each gets its own words, not the
   * search box's "No packages match".
   */
  status?: 'loading' | 'error' | 'ready';
  onRetry?: () => void;
}

/**
 * The tick-list for Edit order, filtered, sorted and paged on screen.
 *
 * Edit order lists the free packages **and** the order's own, two lists the vendor cannot page as
 * one, so it keeps the whole pool in memory. Create order pages on the server instead
 * (`OrderablePackagesStep`, MYP-130). Both draw the same `PackagePicker`, so a rule one enforces
 * the other does too.
 */
export function PackagesStep({ pool, status = 'ready', onRetry, ...rowProps }: PackagesStepProps) {
  const { t } = useTranslation();

  const selected = pool.filter((item) => rowProps.selectedIds.includes(item.id));

  /*
    Search, sort and paging are this screen's own state, not the URL's: they are a way of finding
    a parcel on this step, and coming back to it should show the list, not a stale search. Any
    change of filter goes back to page 1 — staying on page 4 of a list that is now one page long
    would show nothing.
  */
  const [filters, setFilters] = useState<PackagePickFilters>(EMPTY_PACKAGE_PICK_FILTERS);
  const [requestedPage, setPage] = useState(1);
  const changeFilters = (next: PackagePickFilters) => {
    setFilters(next);
    setPage(1);
  };

  const shown = filterPackagePicks(pool, rowProps.selectedIds, filters);
  // Clamped on every render: under "Selected", unticking a row can shrink the list under the page.
  const page = clampPackagePage(requestedPage, shown.length, ORDER_PACKAGES_PER_PAGE);

  const sortLabels: Record<PackagePickSort, string> = {
    default: t('orders.picker.packages.sort.default'),
    tracking: t('orders.picker.packages.sort.tracking'),
    heaviest: t('orders.picker.packages.sort.heaviest'),
  };

  if (status !== 'ready' || pool.length === 0) {
    return <PoolState status={status} onRetry={onRetry} />;
  }

  return (
    <PackagePicker
      {...rowProps}
      search={filters.search}
      searchPlaceholder={t('orders.picker.packages.searchPlaceholder')}
      onSearch={(search) => changeFilters({ ...filters, search })}
      sort={
        <PickerSort
          value={filters.sort}
          defaultValue={EMPTY_PACKAGE_PICK_FILTERS.sort}
          options={PACKAGE_PICK_SORTS.map((value) => ({ value, label: sortLabels[value] }))}
          onChange={(sort) => changeFilters({ ...filters, sort })}
        />
      }
      show={filters.show}
      onShow={(show) => changeFilters({ ...filters, show })}
      allCount={pool.length}
      selected={selected}
      rows={pageOf(shown, page, ORDER_PACKAGES_PER_PAGE)}
      shownTotal={shown.length}
      page={page}
      pageSize={ORDER_PACKAGES_PER_PAGE}
      onPage={setPage}
      onReset={() => changeFilters(EMPTY_PACKAGE_PICK_FILTERS)}
    />
  );
}

export interface PackagePickerProps extends PackageRowsProps {
  search: string;
  searchPlaceholder: string;
  onSearch: (search: string) => void;
  /** The sort control, where the list can be sorted (Edit order only — the vendor cannot sort). */
  sort?: ReactNode;
  show: 'all' | 'selected';
  onShow: (show: 'all' | 'selected') => void;
  /** The "All (n)" count. */
  allCount: number;
  /** Every ticked package, for the "Selected (n)" chip and the totals. */
  selected: readonly OrderablePackage[];
  /** The rows on this page. */
  rows: readonly OrderablePackage[];
  /** How many rows the current view has across all pages. */
  shownTotal: number;
  page: number;
  pageSize: number;
  onPage: (page: number) => void;
  /** Given where the page size can change (Create order). */
  onPageSize?: (size: number) => void;
  /** A new page is on its way; the old one stays on screen, marked busy. */
  busy?: boolean;
  onReset: () => void;
}

/**
 * The tick-list of packages, with its running totals and its limit — drawn the same for Create
 * and Edit order. Two copies of a list with a selection cap, a weight total and a file input is
 * exactly the pair that drifts, and the drift shows up as one screen enforcing a rule the other
 * does not.
 */
export function PackagePicker({
  search,
  searchPlaceholder,
  onSearch,
  sort,
  show,
  onShow,
  allCount,
  selected,
  rows,
  shownTotal,
  page,
  pageSize,
  onPage,
  onPageSize,
  busy = false,
  onReset,
  selectedIds,
  receipts = {},
  onToggle,
  onAttach,
  receiptsLocked = false,
  onRetryReceipt,
  onRemoveReceipt,
  wantsReceipt,
  receiptReason,
  savedReceipts = {},
}: PackagePickerProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const atLimit = selectedIds.length >= MAX_PACKAGES_PER_ORDER;

  return (
    <>
      {/*
        MYP-131: search, chips and sort in one row on a laptop, so the list starts higher.
        phone order layout: one row on a phone too — the chips shrink to "All 30" and a tick with the count
        (their full names stay for screen readers), and the sort becomes an icon.
      */}
      <div className="mb-3 flex items-center gap-2 sm:flex-wrap sm:gap-3">
        <PickerSearch
          label={t('orders.picker.packages.searchLabel')}
          placeholder={searchPlaceholder}
          value={search}
          onChange={onSearch}
          className="flex-1 sm:w-80 sm:flex-none"
        />
        <PickerChips
          label={t('orders.picker.packages.showLabel')}
          value={show}
          options={[
            {
              value: 'all',
              label: t('orders.picker.packages.showAll', { count: allCount }),
              short: t('orders.picker.packages.showAllShort', { count: allCount }),
            },
            {
              value: 'selected',
              label: t('orders.picker.packages.showSelected', { count: selected.length }),
              short: (
                <>
                  <Icon name="check" size={16} />
                  {fmt.number(selected.length)}
                </>
              ),
            },
          ]}
          onChange={onShow}
          className="shrink-0 flex-nowrap gap-1.5 sm:flex-wrap sm:gap-2"
        />
        {sort ? <div className="sm:ml-auto">{sort}</div> : null}
      </div>

      <Card className={cn('p-0 transition-opacity', busy && 'opacity-60')} aria-busy={busy}>
        {shownTotal === 0 && !busy ? (
          <PickerEmpty
            message={
              show === 'selected' && search.trim() === ''
                ? t('orders.picker.packages.noneSelected')
                : t('orders.picker.packages.noMatch')
            }
            onReset={onReset}
          />
        ) : null}
        <div className="divide-y divide-line">
          {rows.map((item) => (
            <PackageRow
              key={item.id}
              item={item}
              selected={selectedIds.includes(item.id)}
              selectable={!atLimit}
              receipts={receipts[item.id]}
              onToggle={onToggle}
              onAttach={onAttach}
              receiptsLocked={receiptsLocked}
              onRetry={onRetryReceipt}
              onRemove={onRemoveReceipt}
              wantsReceipt={wantsReceipt ? wantsReceipt(item) : true}
              savedReceipts={savedReceipts[item.id]}
            />
          ))}
        </div>

        {/* The totals live in the bottom bar (MYP-131), so the list ends with its last row. */}
      </Card>

      <Pager
        label={t('orders.picker.packages.pagerLabel')}
        page={page}
        pageCount={packagePageCount(shownTotal, pageSize)}
        rangeStart={(page - 1) * pageSize + 1}
        rangeEnd={(page - 1) * pageSize + rows.length}
        total={shownTotal}
        onGo={onPage}
        pageSize={onPageSize ? pageSize : undefined}
        onPageSize={onPageSize}
      />

      {onAttach && receiptReason ? (
        <p className="mt-3 border-l-2 border-brand pl-3 text-sm text-ink">
          {receiptReason === 'insurance'
            ? t('orders.newOrder.receipt.insuranceHint')
            : t('orders.newOrder.receipt.requiredHint')}
        </p>
      ) : null}

      <p className="mt-2 text-sm text-muted">{t('orders.newOrder.selectionHint')}</p>

      {atLimit ? (
        <p className="mt-3 text-sm font-semibold text-warning">
          {t('orders.newOrder.limitReached', { max: fmt.number(MAX_PACKAGES_PER_ORDER) })}
        </p>
      ) : null}
    </>
  );
}

/**
 * A list with nothing to show yet, and why (MYP-90): still loading, failed (with a retry), or
 * nothing at all can be ordered.
 */
export function PoolState({
  status,
  onRetry,
}: {
  status: 'loading' | 'error' | 'ready';
  onRetry?: () => void;
}) {
  const { t } = useTranslation();

  if (status === 'loading') {
    return <PoolMessage icon="hourglass_empty" message={t('orders.picker.packages.loading')} />;
  }

  if (status === 'error') {
    return (
      <PoolMessage
        icon="cloud_off"
        message={t('orders.picker.packages.failed')}
        action={
          onRetry ? (
            <button type="button" onClick={onRetry} className={buttonClass('secondary')}>
              <Icon name="refresh" size={18} />
              {t('orders.picker.packages.retry')}
            </button>
          ) : undefined
        }
      />
    );
  }

  return (
    <PoolMessage
      icon="inventory_2"
      message={t('orders.picker.packages.noneOrderable')}
      detail={t('orders.picker.packages.noneOrderableBody')}
      action={
        <Link to={PACKAGES_ROUTE} className={buttonClass('secondary')}>
          {t('orders.picker.packages.goToPackages')}
        </Link>
      }
    />
  );
}

/** The card a `PoolState` draws. */
function PoolMessage({
  icon,
  message,
  detail,
  action,
}: {
  icon: string;
  message: string;
  detail?: string;
  action?: ReactNode;
}) {
  return (
    <Card className="flex flex-col items-center gap-3 px-5 py-10 text-center">
      <Icon name={icon} size={28} className="text-muted" />
      <p className="text-sm font-semibold text-ink">{message}</p>
      {detail ? <p className="max-w-md text-sm text-muted">{detail}</p> : null}
      {action}
    </Card>
  );
}
